import { createColumnHelper } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { pipe } from 'fp-ts/function';
import { record } from 'fp-ts';
import { Restriction, RestrictionOrigin } from '@mews-commander/data-access';
import { WithServiceId, WithTimeUnit } from '@mews-commander/types';
import { LocalizedLabel, LocalizedTextTag, SpacedColon } from '@mews-commander/ui';
import { TimeUnit, UnicodeEntity } from '@mews/enums';
import { LocalizedEnum, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Typography, Stack, StackItem, TypographyComponentProps } from '@optimus-web/core';
import { DateRangeLabel, DayRanges, exceptionItemFactory } from '../../common';
import { RestrictionPreviewRowData } from '../types';
import { RateGroupName, RateName, ResourceCategoryName } from '../../../../../components/commander-ui';
import { isValidNumberRange, isValidPriceRange } from '../../../utils/exceptions/getCellExceptions';
import reshapeExceptionsObject from '../../../utils/exceptions/reshapeExceptionsObject';
import processExceptionValues from '../../../utils/exceptions/processExceptionValues';

interface Config extends Partial<WithTimeUnit>, WithServiceId {
    isServiceDayUse: boolean;
}

const columnHelper = createColumnHelper<RestrictionPreviewRowData>();

const actionTagPropsMap: Record<RestrictionPreviewRowData['action'], Parameters<typeof LocalizedTextTag>[0]> = {
    create: { textKey: TextKey.PreviewCreateTag, variant: 'success' },
    delete: { textKey: TextKey.PreviewDeleteTag, variant: 'danger' },
    keep: { textKey: TextKey.PreviewNoChangesTag, variant: 'warning' },
};

const typeToTextKeyMap: Record<Restriction['conditions']['type'], Mews.TextKey> = {
    ClosedToArrival: TextKey.ClosedToArrival,
    ClosedToDeparture: TextKey.ClosedToDeparture,
    ClosedToStay: TextKey.ClosedToStay,
};

interface ConditionCellProps {
    value: NonNullable<ReactNode>;
    labelTextKey: Mews.TextKey;
}

const ConditionCell = ({ value, labelTextKey }: ConditionCellProps) => (
    <Stack vertical>
        <Typography textStyle="bodyMediumStrong" as="div">
            {value}
        </Typography>
        <LocalizedLabel textKey={labelTextKey} variant="secondary" as="div" />
    </Stack>
);

const ExceptionItem = exceptionItemFactory({
    ExceptionItemWrapper: ({ children }: { children: ReactNode }) => (
        <Stack itemAlignment="center" spacing="200">
            {children}
        </Stack>
    ),
    LabelElement: LocalizedLabel,
    ValueElement: (props: TypographyComponentProps) => <Typography textStyle="bodyMediumStrong" {...props} as="div" />,
});

export const createColumnMapping = ({ timeUnit = TimeUnit.Day, isServiceDayUse, serviceId }: Config) => [
    columnHelper.display({
        id: 'action',
        header: () => <LocalizedText textKey={TextKey.Action} />,
        cell: ({ row }) => <LocalizedTextTag {...actionTagPropsMap[row.original.action]} />,
    }),
    columnHelper.display({
        id: 'datesAndType',
        header: () => (
            <span>
                <LocalizedText textKey={TextKey.DatePlural} />
                {UnicodeEntity.ForwardSlash}
                <LocalizedText textKey={TextKey.Type} />
            </span>
        ),
        cell: ({ row }) => {
            const { startUtc, endUtc, type, daysNew } = row.original.conditions;
            return (
                <ConditionCell
                    value={
                        <Stack vertical>
                            <StackItem>
                                <DateRangeLabel startUtc={startUtc} endUtc={endUtc} />
                            </StackItem>
                            {daysNew &&
                            pipe(
                                daysNew,
                                record.filterWithIndex((_, enabled) => enabled),
                                (value) => record.size(value) !== 7
                            ) ? (
                                <span>
                                    <LocalizedText textKey={TextKey.Days} />
                                    {SpacedColon}
                                    <DayRanges days={daysNew} />
                                </span>
                            ) : null}
                        </Stack>
                    }
                    labelTextKey={typeToTextKeyMap[type]}
                />
            );
        },
    }),
    columnHelper.display({
        id: 'rates',
        header: () => <LocalizedText textKey={TextKey.RatePlural} />,
        cell: ({ row }) => {
            const { rateGroupId, baseRateId, exactRateId } = row.original.conditions;
            if (rateGroupId) {
                return (
                    <ConditionCell
                        value={<RateGroupName rateGroupId={rateGroupId} />}
                        labelTextKey={TextKey.RateGroup}
                    />
                );
            }
            if (baseRateId) {
                return <ConditionCell value={<RateName rateId={baseRateId} />} labelTextKey={TextKey.BaseRate} />;
            }
            if (exactRateId) {
                return <ConditionCell value={<RateName rateId={exactRateId} />} labelTextKey={TextKey.ExactRate} />;
            }
            return <LocalizedLabel textKey={TextKey.AllRates} as="div" />;
        },
    }),
    columnHelper.display({
        id: 'spaces',
        header: () => <LocalizedText textKey={TextKey.SpacePlural} />,
        cell: ({ row }) => {
            const { resourceCategoryType, resourceCategoryId } = row.original.conditions;
            if (resourceCategoryType) {
                return (
                    <ConditionCell
                        value={<LocalizedEnum prefix="ResourceCategoryType" value={resourceCategoryType} />}
                        labelTextKey={TextKey.SpaceType}
                    />
                );
            }
            if (resourceCategoryId) {
                return (
                    <ConditionCell
                        value={<ResourceCategoryName id={resourceCategoryId} serviceId={serviceId} />}
                        labelTextKey={TextKey.SpaceCategory}
                    />
                );
            }
            return <LocalizedLabel textKey={TextKey.AllSpaces} as="div" />;
        },
    }),
    columnHelper.display({
        id: 'exceptions',
        header: () => <LocalizedText textKey={TextKey.Exceptions} />,
        cell: ({ row }) => {
            const { advance, length, price, reservationCount, secondaryRange } = pipe(
                processExceptionValues(row.original, timeUnit),
                reshapeExceptionsObject
            );

            return (
                <Stack vertical>
                    {advance ? (
                        <ExceptionItem
                            type="advance"
                            range={advance}
                            showIcon={!isValidNumberRange(advance)}
                            timeUnit={timeUnit}
                        />
                    ) : null}
                    {length ? (
                        <ExceptionItem
                            showIcon={!isValidNumberRange(length)}
                            type="length"
                            range={length}
                            timeUnit={timeUnit}
                            isServiceDayUse={isServiceDayUse}
                            secondaryRange={secondaryRange}
                        />
                    ) : null}
                    {price ? (
                        <ExceptionItem
                            type="price"
                            range={price}
                            showIcon={!isValidPriceRange(price)}
                            timeUnit={timeUnit}
                        />
                    ) : null}
                    {reservationCount ? (
                        <ExceptionItem
                            type="reservationCount"
                            range={reservationCount}
                            showIcon={!isValidNumberRange(reservationCount)}
                            timeUnit={timeUnit}
                        />
                    ) : null}
                </Stack>
            );
        },
    }),
];

export const originColumn = columnHelper.display({
    id: 'origin',
    header: () => <LocalizedText textKey={TextKey.Origin} />,
    cell: ({ row }) => (
        <>
            {row.original.origin === RestrictionOrigin.User ? (
                'Mews'
            ) : (
                <LocalizedText textKey={TextKey.EnterpriseIntegration} />
            )}
        </>
    ),
});
